import { useState, useEffect, FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  PawPrint, CalendarDays, Stethoscope, ShoppingCart, Boxes, Receipt, MessageCircle,
  Building2, Scissors, GraduationCap, Truck, Footprints, BarChart3, Users, ShieldCheck,
  Sparkles, CheckCircle2, Mail, Phone, ArrowRight, Menu, X, Smartphone, BellRing, Award,
  Star, LogIn, Loader2, Search, Rocket, HelpCircle, ArrowLeft, ChevronDown,
} from "lucide-react";
import { api, mensajeError } from "../api/client";
import { useRevelarAlEntrar } from "../hooks/useRevelarAlEntrar";
import { BASE_DOMAIN } from "../lib/subdomain";
import { registrarVista, registrarNavegacion, registrarClic } from "../lib/analitica";
import { IlustracionHero, IlustracionPortal, IlustracionBlobDecorativo } from "../components/landing/Ilustraciones";
import { DomicilioCampos, type DomicilioValores } from "../components/ui/DomicilioCampos";

// El login/panel vive en el subdominio de la app (ej. app.mascotas.vet), no
// en el dominio raíz donde se sirve este portal de inicio.

// Esta página vive fuera del login de cada negocio, así que su paleta se
// define de forma explícita aquí mismo (clases de Tailwind), sin tocar los
// botones/colores compartidos del panel (esos siguen usando el color de
// marca de cada negocio, definido en tailwind.config.js).
//
// Paleta oficial del manual de identidad "Huella pixelada" v2.0 (agosto
// 2026): los 5 tonos de azul definidos en tailwind.config.js (azul-profundo,
// azul-trazo, azul-medio, azul-suave, azul-claro), sin mezclar otros colores.
// La variedad viene de repartir esos tonos entre botones, banner, franja de
// precios y sección de demo, y de que ninguna sección tiene un borde recto:
// los cambios de fondo entre secciones siempre son degradados o franjas
// difuminadas hacia blanco/azul-claro.
const BTN_PRIMARIO = "inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-azul-trazo to-azul-suave px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:brightness-110 hover:shadow-md hover:shadow-azul-suave hover:-translate-y-0.5";
const BTN_SECUNDARIO = "inline-flex items-center justify-center gap-2 rounded-full border-2 border-azul-suave bg-white px-4 py-2 text-sm font-semibold text-azul-profundo shadow-sm transition-all duration-200 hover:border-azul-suave hover:bg-azul-claro hover:-translate-y-0.5";
const CAMPO_INPUT = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-azul-medio focus:border-transparent placeholder:text-slate-400";
const CAMPO_LABEL = "block text-xs font-medium text-slate-500 mb-1";

// Foto real (no ilustración) con licencia libre de Unsplash — se usa como
// muestra dentro del hero para responder a "cómo insertar imágenes reales
// de veterinarios y animales" con un ejemplo funcionando. Crédito: Alexander
// Mass en Unsplash (licencia Unsplash, uso libre, se agradece atribución).
const FOTO_VETERINARIO_URL = "/veterinario-mascotas-vet.png";
const TIPOS_NEGOCIO = [
  { value: "CLINICA", label: "Clínica veterinaria" },
  { value: "PET_SHOP", label: "Pet shop" },
  { value: "ESTETICA", label: "Estética canina" },
  { value: "HOTEL", label: "Hotel para mascotas" },
  { value: "GUARDERIA", label: "Guardería canina" },
  { value: "ENTRENAMIENTO", label: "Entrenamiento canino" },
  { value: "MIXTO", label: "Negocio mixto / varios servicios" },
];

// Meta Pixel (para medir la publicidad en redes sociales del portal de
// inicio). Antes vivía fijo en index.html, pero ese mismo archivo/bundle
// también sirve el portal interno de cada negocio (subdominios) — cargarlo
// ahí no tiene sentido (nadie anuncia el login de un negocio) y además
// generaba un error inofensivo pero confuso en la consola cuando el
// navegador del usuario bloquea rastreadores. Por eso se carga solo aquí,
// una vez, cuando el portal de inicio realmente se monta.
let pixelCargado = false;
function cargarMetaPixel() {
  if (pixelCargado || typeof window === "undefined") return;
  pixelCargado = true;
  const w = window as any;
  (function (f: any, b: Document, e: string, v: string, n: any, t: HTMLScriptElement, s: Element | null) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s?.parentNode?.insertBefore(t, s);
  })(w, document, "script", "https://connect.facebook.net/en_US/fbevents.js", undefined, undefined as any, null);
  w.fbq("init", "711952562328651");
  w.fbq("track", "PageView");
}

// WhatsApp de contacto del portal (botón flotante y pie de página).
const WHATSAPP_URL = "https://wa.me/523315208423?text=" + encodeURIComponent("Hola, me gustaría información sobre Mascotas Vet.");

function Revelar({ children, className = "" }: { children: ReactNode; className?: string }) {
  const { ref, visible } = useRevelarAlEntrar<HTMLDivElement>();
  return (
    <div ref={ref} className={`revelar ${visible ? "visible" : ""} ${className}`}>
      {children}
    </div>
  );
}

// Contador de negocios para el banner "Forma parte de los +X negocios...".
// Cuando el banner entra en pantalla, sube en cámara rápida desde un número
// menor hasta la meta (efecto "llamativo"), y después le da vida con
// pequeños incrementos ocasionales — como si se estuvieran dando de alta
// negocios nuevos en ese momento. Es un contador de exhibición (no llama a
// ningún endpoint), pensado como gancho de marketing en la página pública.
const NEGOCIOS_META = 812;
const NEGOCIOS_INICIO = 640;

function ContadorNegocios() {
  const { ref, visible } = useRevelarAlEntrar<HTMLSpanElement>();
  const [valor, setValor] = useState(NEGOCIOS_INICIO);

  useEffect(() => {
    if (!visible) return;
    const t0 = performance.now();
    const duracionMs = 2200;
    let frame: number;
    function animar(t: number) {
      const progreso = Math.min(1, (t - t0) / duracionMs);
      const suavizado = 1 - Math.pow(1 - progreso, 3); // ease-out
      setValor(Math.round(NEGOCIOS_INICIO + (NEGOCIOS_META - NEGOCIOS_INICIO) * suavizado));
      if (progreso < 1) frame = requestAnimationFrame(animar);
    }
    frame = requestAnimationFrame(animar);
    return () => cancelAnimationFrame(frame);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    // Después de llegar a la meta, un tic ocasional (cada 15-25s) le da
    // sensación de actividad en vivo, sin exagerar.
    const id = setInterval(() => setValor((v) => v + 1), 15000 + Math.random() * 10000);
    return () => clearInterval(id);
  }, [visible]);

  return (
    <span ref={ref} className="tabular-nums">
      {valor.toLocaleString("es-MX")}
    </span>
  );
}

// Rotación de 3 tonos de azul (medio, trazo, profundo — de la paleta oficial
// de marca) que se reparte en las cuadrículas de la página — módulos, franja
// de confianza y testimonios — para dar jerarquía visual sin salirse nunca
// de la familia de azules del manual de identidad. Las clases van completas
// y literales (no armadas con `${}`) a propósito: Tailwind solo genera el
// CSS de las clases que puede leer tal cual en el código fuente.
const ACENTOS = [
  { suave: "bg-azul-claro", fuerte: "text-azul-medio", solido: "bg-azul-medio", solidoAlHover: "group-hover:bg-azul-medio", borde: "hover:border-azul-claro" },
  { suave: "bg-azul-claro", fuerte: "text-azul-trazo", solido: "bg-azul-trazo", solidoAlHover: "group-hover:bg-azul-trazo", borde: "hover:border-azul-claro" },
  { suave: "bg-azul-claro", fuerte: "text-azul-profundo", solido: "bg-azul-profundo", solidoAlHover: "group-hover:bg-azul-profundo", borde: "hover:border-azul-claro" },
];

const MODULOS = [
  { icono: CalendarDays, titulo: "Agenda y citas", texto: "Calendario visual por día y semana, con catálogo de servicios para agendar en segundos." },
  { icono: Stethoscope, titulo: "Expediente clínico digital", texto: "Consultas, vacunas, recetas y documentos de cada mascota en una sola línea de tiempo." },
  { icono: ShoppingCart, titulo: "Punto de venta", texto: "Cobra productos y servicios, con corte de caja al final del día." },
  { icono: Boxes, titulo: "Productos y Servicios", texto: "Control de stock con alertas de caducidad y stock bajo. Carga o descarga todo tu catálogo por CSV." },
  { icono: Receipt, titulo: "Facturación CFDI", texto: "Genera facturas electrónicas para los clientes que las necesiten." },
  { icono: MessageCircle, titulo: "Recordatorios automáticos", texto: "WhatsApp y correo para confirmar citas, avisar próximas vacunas y evitar inasistencias." },
  { icono: Building2, titulo: "Hospedaje y guardería", texto: "Control de espacios/habitaciones y estancias, con notas por cada huésped." },
  { icono: Scissors, titulo: "Estética y grooming", texto: "Agenda y seguimiento de baños, cortes y tratamientos." },
  { icono: GraduationCap, titulo: "Entrenamiento", texto: "Cursos, inscripciones y sesiones para tus clases de adiestramiento." },
  { icono: Truck, titulo: "Servicio a domicilio", texto: "Programa y da seguimiento a las visitas fuera de tu negocio." },
  { icono: Footprints, titulo: "Paseos", texto: "Organiza paseadores, rutas y mascotas asignadas." },
  { icono: BarChart3, titulo: "Reportes y finanzas", texto: "Ventas, ingresos e indicadores de tu negocio siempre a la mano." },
  { icono: Users, titulo: "Roles de usuario", texto: "Administradores, veterinarios y recepción, cada quien con el acceso que le corresponde." },
  { icono: ShieldCheck, titulo: "Multi-negocio", texto: "Cada sucursal con sus propios datos, completamente aislados y seguros." },
];

// Testimonios simulados (marcador de ejemplo, tal como se pidió) — nombres y
// negocios ficticios, con iniciales en vez de fotos para no fabricar
// "evidencia" fotográfica de personas reales que no existen.
const TESTIMONIOS = [
  { iniciales: "MG", nombre: "Mariana Gómez", negocio: "Clínica Veterinaria San Ángel", texto: "Migramos de hojas de cálculo a Mascotas en una tarde. Los recordatorios automáticos por WhatsApp bajaron muchísimo las inasistencias." },
  { iniciales: "RC", nombre: "Roberto Castillo", negocio: "Pet Shop La Huella", texto: "El inventario con carga por CSV nos ahorra horas cada semana. Por fin sabemos qué se nos está por caducar." },
  { iniciales: "AL", nombre: "Ana Lucía Reyes", negocio: "Estética Canina Pelusa", texto: "Nuestros clientes aman poder ver el expediente de su mascota desde el celular, con nuestro logo y todo." },
  { iniciales: "JT", nombre: "Jorge Torres", negocio: "Hotel y Guardería PawStay", texto: "Tiene todo lo que usábamos en el sistema de la cadena donde trabajaba antes, pero a un precio que sí podemos pagar." },
];

export default function Landing() {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [form, setForm] = useState({ nombre: "", negocio: "", telefono: "", email: "", mensaje: "" });
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Analítica: se registra UNA vista al cargar el portal de inicio (ver
  // src/lib/analitica.ts — es anónimo, solo cuenta visitas/visitantes
  // únicos, sin guardar nada identificable).
  useEffect(() => {
    registrarVista();
    cargarMetaPixel();
  }, []);

  // "Ya soy cliente": localiza el negocio del correo capturado y redirige a
  // su login, sin exponer nunca una lista pública de negocios/subdominios.
  const [modalClienteAbierto, setModalClienteAbierto] = useState(false);
  const [correoCliente, setCorreoCliente] = useState("");
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [errorCliente, setErrorCliente] = useState<string | null>(null);
  const [negociosEncontrados, setNegociosEncontrados] = useState<{ subdominio: string; nombre: string }[] | null>(null);

  function abrirModalCliente() {
    registrarClic("Ya soy cliente");
    setModalClienteAbierto(true);
  }

  function cerrarModalCliente() {
    setModalClienteAbierto(false);
    setCorreoCliente("");
    setErrorCliente(null);
    setNegociosEncontrados(null);
    setBuscandoCliente(false);
  }

  function irALogin(subdominio: string) {
    window.location.href = `https://${subdominio}.${BASE_DOMAIN}/login?correo=${encodeURIComponent(correoCliente)}`;
  }

  async function buscarNegocio(e: FormEvent) {
    e.preventDefault();
    registrarClic("Ya soy cliente - Buscar");
    setBuscandoCliente(true);
    setErrorCliente(null);
    setNegociosEncontrados(null);
    try {
      const res = await api.post("/auth/localizar-negocio", { email: correoCliente });
      const negocios: { subdominio: string; nombre: string }[] = res.data?.negocios ?? [];
      if (negocios.length === 0) {
        setErrorCliente("No encontramos ninguna cuenta con ese correo. Revisa que esté bien escrito o solicita una cuenta demo.");
      } else if (negocios.length === 1) {
        irALogin(negocios[0].subdominio);
      } else {
        setNegociosEncontrados(negocios);
      }
    } catch (err) {
      setErrorCliente(mensajeError(err));
    } finally {
      setBuscandoCliente(false);
    }
  }

  // "Quiero consultar el expediente de mi mascota": busca por correo Y
  // teléfono (ambos deben coincidir) entre todos los negocios de la red, sin
  // pedirle al cliente que recuerde el enlace con token que le mandamos por
  // WhatsApp/correo cuando se dio de alta la mascota.
  const [contacto, setContacto] = useState({ email: "", telefono: "" });
  const [buscandoExpediente, setBuscandoExpediente] = useState(false);
  const [errorExpediente, setErrorExpediente] = useState<string | null>(null);
  const [expedientesEncontrados, setExpedientesEncontrados] = useState<
    { id: string; nombre: string; especie: string; tokenPublico: string; negocioNombre: string; negocioSubdominio: string }[] | null
  >(null);
  const [sinResultadosExpediente, setSinResultadosExpediente] = useState(false);

  function irAExpediente(subdominio: string, token: string) {
    window.location.href = `https://${subdominio}.${BASE_DOMAIN}/portal-mascota/${token}`;
  }

  async function buscarExpediente(e: FormEvent) {
    e.preventDefault();
    registrarClic("Buscar mi expediente");
    setBuscandoExpediente(true);
    setErrorExpediente(null);
    setExpedientesEncontrados(null);
    setSinResultadosExpediente(false);
    try {
      const res = await api.post("/portal-mascota/buscar-por-contacto", contacto);
      const items = res.data?.items ?? [];
      if (items.length === 0) {
        setSinResultadosExpediente(true);
      } else if (items.length === 1) {
        irAExpediente(items[0].negocioSubdominio, items[0].tokenPublico);
      } else {
        setExpedientesEncontrados(items);
      }
    } catch (err) {
      setErrorExpediente(mensajeError(err));
    } finally {
      setBuscandoExpediente(false);
    }
  }

  async function enviarSolicitud(e: FormEvent) {
    e.preventDefault();
    registrarClic("Solicitar demo - Formulario");
    setEnviando(true);
    setError(null);
    try {
      await api.post("/demo/solicitar", form);
      setEnviado(true);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  // "Ya quiero usarlo": alta de negocio con pago en línea. No se crea nada
  // todavía al enviar el formulario — se manda a Stripe a pagar, y el negocio
  // solo se crea de verdad cuando el pago se confirma (ver /alta/completado).
  const [modalAltaAbierto, setModalAltaAbierto] = useState(false);
  // El formulario se llena en dos pasos cortos (en vez de uno solo muy largo):
  // 1) datos del negocio y del administrador, 2) domicilio. Así se ve
  // ordenado tanto en celular como en computadora.
  const [pasoAlta, setPasoAlta] = useState<1 | 2>(1);
  const [formAlta, setFormAlta] = useState({
    nombreNegocio: "",
    subdominio: "",
    tipoNegocio: "MIXTO",
    nombreAdmin: "",
    email: "",
    telefono: "",
    password: "",
    plan: "MENSUAL" as "MENSUAL" | "ANUAL",
  });
  const [domicilioAlta, setDomicilioAlta] = useState<DomicilioValores>({
    calle: "",
    numero: "",
    codigoPostal: "",
    colonia: "",
    ciudad: "",
    estado: "",
    pais: "México",
  });
  const [enviandoAlta, setEnviandoAlta] = useState(false);
  const [errorAlta, setErrorAlta] = useState<string | null>(null);
  const [erroresAlta, setErroresAlta] = useState<Record<string, string>>({});
  const [ayudaSubdominioAbierta, setAyudaSubdominioAbierta] = useState(false);

  function abrirModalAlta(plan: "MENSUAL" | "ANUAL" = "MENSUAL", origen = "Header") {
    registrarClic(`Ya quiero usarlo - ${origen}`);
    setFormAlta({
      nombreNegocio: "",
      subdominio: "",
      tipoNegocio: "MIXTO",
      nombreAdmin: "",
      email: "",
      telefono: "",
      password: "",
      plan,
    });
    setDomicilioAlta({ calle: "", numero: "", codigoPostal: "", colonia: "", ciudad: "", estado: "", pais: "México" });
    setErrorAlta(null);
    setErroresAlta({});
    setPasoAlta(1);
    setModalAltaAbierto(true);
  }

  // Permite mandar a alguien DIRECTO al formulario de alta desde un enlace
  // (por ejemplo, en redes sociales o un anuncio), sin que tenga que buscar
  // el botón en la página. Con https://mascotas.vet/?alta=1 se abre el
  // formulario en cuanto carga la página, ya en el plan mensual; con
  // ?alta=anual se abre directo en el plan anual.
  useEffect(() => {
    const parametro = new URLSearchParams(window.location.search).get("alta");
    if (!parametro) return;
    abrirModalAlta(parametro.toLowerCase() === "anual" ? "ANUAL" : "MENSUAL", "Enlace directo");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cambiarAlta(campo: string, valor: string) {
    setFormAlta((f) => ({ ...f, [campo]: valor }));
  }

  // El subdominio nunca debe poder llevar espacios, acentos, comas ni otros
  // símbolos (tiene que quedar limpio para usarse como parte de una URL):
  // en vez de solo avisar después, se descartan esos caracteres al momento
  // de escribirlos, para que nunca lleguen a aparecer en el campo.
  function cambiarSubdominio(valor: string) {
    const limpio = valor
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "") // quita acentos (á, é, ñ, etc.)
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, ""); // deja solo minúsculas, números y guion
    cambiarAlta("subdominio", limpio);
  }

  function cambiarDomicilioAlta(campo: keyof DomicilioValores, valor: string) {
    setDomicilioAlta((d) => ({ ...d, [campo]: valor }));
  }

  // Todos los campos son obligatorios: se valida aquí mismo (antes de llamar
  // al backend) para poder marcar en rojo exactamente el campo que falta y
  // llevar al prospecto directo ahí, en vez de un error genérico. Se valida
  // por separado cada paso del formulario (datos del negocio, y domicilio).
  function validarPaso1(): boolean {
    const errores: Record<string, string> = {};
    if (!formAlta.nombreNegocio.trim()) errores.nombreNegocio = "Ingresa el nombre de tu negocio";
    if (!formAlta.subdominio.trim()) errores.subdominio = "Elige un subdominio para tu portal";
    else if (formAlta.subdominio.trim().length < 3) errores.subdominio = "Debe tener al menos 3 caracteres";
    if (!formAlta.nombreAdmin.trim()) errores.nombreAdmin = "Ingresa tu nombre";
    if (!formAlta.telefono.trim()) errores.telefono = "Ingresa un teléfono de contacto";
    if (!formAlta.email.trim()) errores.email = "Ingresa tu correo";
    if (!formAlta.password || formAlta.password.length < 6) errores.password = "Mínimo 6 caracteres";

    setErroresAlta(errores);
    if (Object.keys(errores).length > 0) {
      const primerCampo = Object.keys(errores)[0];
      document.querySelector(`[data-campo="${primerCampo}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  }

  function validarDomicilioAlta(): boolean {
    const errores: Record<string, string> = {};
    if (!domicilioAlta.calle.trim()) errores.calle = "Ingresa la calle";
    if (!domicilioAlta.numero.trim()) errores.numero = "Ingresa el número";
    if (!/^\d{5}$/.test(domicilioAlta.codigoPostal)) errores.codigoPostal = "Debe tener 5 dígitos";
    if (!domicilioAlta.colonia.trim()) errores.colonia = "Ingresa la colonia";
    if (!domicilioAlta.ciudad.trim()) errores.ciudad = "Ingresa la ciudad o municipio";
    if (!domicilioAlta.estado.trim()) errores.estado = "Ingresa el estado";
    if (!domicilioAlta.pais.trim()) errores.pais = "Ingresa el país";

    setErroresAlta(errores);
    if (Object.keys(errores).length > 0) {
      const primerCampo = Object.keys(errores)[0];
      document.querySelector(`[data-campo="${primerCampo}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  }

  // El botón "Continuar al pago" del paso 2 se habilita solo hasta que el
  // domicilio está completo — así queda claro, de un vistazo, qué falta
  // antes de poder pagar, sin tener que dar clic para descubrirlo.
  const domicilioAltaCompleto =
    !!domicilioAlta.calle.trim() &&
    !!domicilioAlta.numero.trim() &&
    /^\d{5}$/.test(domicilioAlta.codigoPostal) &&
    !!domicilioAlta.colonia.trim() &&
    !!domicilioAlta.ciudad.trim() &&
    !!domicilioAlta.estado.trim() &&
    !!domicilioAlta.pais.trim();

  function irAPaso2(e: FormEvent) {
    e.preventDefault();
    setErrorAlta(null);
    if (validarPaso1()) {
      registrarClic("Alta - Continuar paso 1");
      setErroresAlta({});
      setPasoAlta(2);
    }
  }

  function volverAPaso1() {
    setErroresAlta({});
    setPasoAlta(1);
  }

  async function enviarAlta(e: FormEvent) {
    e.preventDefault();
    setErrorAlta(null);
    if (!validarDomicilioAlta()) return;

    registrarClic("Alta - Continuar al pago");
    setEnviandoAlta(true);
    try {
      const res = await api.post("/auth/alta/iniciar", {
        ...formAlta,
        calle: domicilioAlta.calle,
        numero: domicilioAlta.numero,
        codigoPostal: domicilioAlta.codigoPostal,
        colonia: domicilioAlta.colonia,
        ciudad: domicilioAlta.ciudad,
        estadoDireccion: domicilioAlta.estado,
        pais: domicilioAlta.pais,
      });
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      setErrorAlta(mensajeError(err));
      setEnviandoAlta(false);
    }
  }

  // Clase para marcar en rojo un campo con error, y su mensaje debajo.
  function claseCampoAlta(campo: string) {
    return erroresAlta[campo] ? `${CAMPO_INPUT} border-red-400 focus:ring-red-300` : CAMPO_INPUT;
  }
  function ErrorAlta({ campo }: { campo: string }) {
    if (!erroresAlta[campo]) return null;
    return <p className="mt-1 text-xs text-red-600">{erroresAlta[campo]}</p>;
  }

  return (
    <div className="min-h-screen bg-white text-slate-800 overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/90 shadow-[0_1px_0_0_rgba(4,44,83,0.06)] backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="#inicio" className="flex items-center">
            <img src="/logo-mascotas-vet-horizontal.png" alt="Mascotas Vet" className="h-11 w-auto sm:h-12" />
          </a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 lg:flex">
            <a href="#modulos" onClick={() => registrarNavegacion("Módulos")} className="hover:text-azul-trazo">Módulos</a>
            <a href="#precios" onClick={() => registrarNavegacion("Precios")} className="hover:text-azul-trazo">Precios</a>
            <a href="#opiniones" onClick={() => registrarNavegacion("Opiniones")} className="hover:text-azul-trazo">Opiniones</a>
            <div className="group relative">
              <button type="button" className="flex items-center gap-1 hover:text-azul-trazo">
                Recursos <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full z-50 w-52 -translate-x-1/2 pt-3 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white py-1.5 text-slate-600 shadow-lg">
                  <a href="#portal" onClick={() => registrarNavegacion("Portal del cliente")} className="px-4 py-2 hover:bg-azul-claro hover:text-azul-trazo">Portal del cliente</a>
                  <a href="#mi-expediente" onClick={() => registrarNavegacion("Mi expediente")} className="px-4 py-2 hover:bg-azul-claro hover:text-azul-trazo">Mi expediente</a>
                  <Link to="/tutoriales" onClick={() => registrarNavegacion("Tutoriales")} className="px-4 py-2 hover:bg-azul-claro hover:text-azul-trazo">Tutoriales</Link>
                </div>
              </div>
            </div>
            <button type="button" onClick={abrirModalCliente} className="inline-flex items-center gap-1.5 hover:text-azul-trazo">
              <LogIn size={15} /> Ya soy cliente
            </button>
          </nav>
          <div className="hidden lg:flex items-center gap-2.5">
            <a href="#demo" onClick={() => registrarClic("Solicitar demo - Header")} className={BTN_SECUNDARIO}>
              Solicitar demo
            </a>
            <button type="button" onClick={() => abrirModalAlta("MENSUAL", "Header")} className={BTN_PRIMARIO}>
              <Rocket size={15} /> Ya quiero usarlo
            </button>
          </div>
          <button className="lg:hidden" onClick={() => setMenuAbierto((v) => !v)} aria-label="Abrir menú">
            {menuAbierto ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuAbierto && (
          <div className="bg-gradient-to-b from-azul-claro/40 to-transparent px-5 pb-4 pt-2 lg:hidden">
            <nav className="flex flex-col gap-3 text-sm font-medium text-slate-600">
              <a href="#modulos" onClick={() => { registrarNavegacion("Módulos"); setMenuAbierto(false); }}>Módulos</a>
              <a href="#precios" onClick={() => { registrarNavegacion("Precios"); setMenuAbierto(false); }}>Precios</a>
              <a href="#opiniones" onClick={() => { registrarNavegacion("Opiniones"); setMenuAbierto(false); }}>Opiniones</a>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Recursos</p>
              <a href="#portal" className="pl-1" onClick={() => { registrarNavegacion("Portal del cliente"); setMenuAbierto(false); }}>Portal del cliente</a>
              <a href="#mi-expediente" className="pl-1" onClick={() => { registrarNavegacion("Mi expediente"); setMenuAbierto(false); }}>Mi expediente</a>
              <Link to="/tutoriales" className="pl-1" onClick={() => { registrarNavegacion("Tutoriales"); setMenuAbierto(false); }}>Tutoriales</Link>
              <button
                type="button"
                className="mt-1 inline-flex items-center gap-1.5 text-left"
                onClick={() => {
                  setMenuAbierto(false);
                  abrirModalCliente();
                }}
              >
                <LogIn size={15} /> Ya soy cliente
              </button>
              <a
                href="#demo"
                className={`${BTN_SECUNDARIO} justify-center`}
                onClick={() => { registrarClic("Solicitar demo - Header"); setMenuAbierto(false); }}
              >
                Solicitar demo
              </a>
              <button
                type="button"
                className={`${BTN_PRIMARIO} justify-center`}
                onClick={() => {
                  setMenuAbierto(false);
                  abrirModalAlta("MENSUAL", "Header");
                }}
              >
                <Rocket size={15} /> Ya quiero usarlo
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Logotipo centralizado */}
      <div className="flex justify-center px-5 py-6">
        <img
          src="/logo-mascotas-vet-horizontal.png"
          alt="Mascotas Vet"
          className="h-auto w-full max-w-xs md:max-w-sm"
        />
      </div>

      {/* Hero */}
      <section id="inicio" className="relative overflow-hidden bg-gradient-to-b from-azul-claro/50 via-white to-white">
        <div className="pointer-events-none absolute -top-20 -left-24 h-72 w-72 text-azul-suave animar-blob">
          <IlustracionBlobDecorativo className="h-full w-full" />
        </div>
        <div className="pointer-events-none absolute -bottom-10 -right-16 h-80 w-80 text-azul-medio/70 animar-blob" style={{ animationDelay: "2s" }}>
          <IlustracionBlobDecorativo className="h-full w-full" />
        </div>
        <div className="pointer-events-none absolute top-1/3 right-1/4 h-40 w-40 text-azul-claro animar-blob" style={{ animationDelay: "1s" }}>
          <IlustracionBlobDecorativo className="h-full w-full" />
        </div>

        <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-24">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-azul-claro px-3 py-1 text-xs font-semibold text-azul-profundo">
              <Sparkles size={13} /> Hecho para veterinarias, pet shops, hoteles y estéticas
            </span>
            <h1 className="mt-4 font-marca font-black text-4xl leading-tight text-slate-800 sm:text-5xl">
              La plataforma robusta de las grandes cadenas,
              <span className="bg-gradient-to-r from-azul-trazo via-azul-medio to-azul-suave bg-clip-text text-transparent"> a un precio adecuado para tu negocio.</span>
            </h1>
            <p className="mt-5 max-w-lg text-lg text-slate-500">
              Mascotas junta agenda, expediente clínico, ventas, productos y servicios, facturación y recordatorios automáticos en
              un solo lugar — con la misma solidez que usan los establecimientos grandes, pensada para el negocio
              pequeño y mediano.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button type="button" onClick={() => abrirModalAlta("MENSUAL", "Hero")} className={`${BTN_PRIMARIO} !px-6 !py-3 !text-base`}>
                <Rocket size={18} /> Ya quiero usarlo
              </button>
              <a href="#demo" onClick={() => registrarClic("Solicitar demo - Hero")} className={`${BTN_SECUNDARIO} !px-6 !py-3 !text-base`}>
                Solicitar cuenta demo
              </a>
            </div>
            <a href="#modulos" onClick={() => registrarNavegacion("Ver módulos (hero)")} className="mt-4 inline-block text-sm font-medium text-azul-trazo hover:underline">
              Ver todos los módulos →
            </a>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-azul-medio" /> Sin instalar nada</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-azul-trazo" /> Funciona en tu celular</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-azul-profundo" /> Cancela cuando quieras</span>
            </div>
          </div>
          <Revelar className="relative">
            <div className="transition-transform duration-500 hover:scale-[1.03]">
              <IlustracionHero />
            </div>
            {/* Ejemplo de foto real (no ilustración) integrada al diseño —
                ver la guía para cómo cambiarla o agregar más. */}
<div
  className="group absolute -bottom-6 -left-4 w-40 -rotate-3 overflow-hidden rounded-2xl border-4 border-white shadow-xl transition-transform duration-300 hover:rotate-0 hover:scale-105 sm:w-48"
>
  <img
    src={FOTO_VETERINARIO_URL}
    alt="Veterinario de Mascotas Vet revisando el expediente de un perro con la app"
    className="h-full w-full object-cover"
    loading="lazy"
  />
</div>
        
          </Revelar>
        </div>
      </section>

      {/* Banner llamativo: contador de negocios que usan la plataforma. El
          degradado azul se desliza despacio (sidebar-degradado) y las dos
          franjas blancas difuminadas arriba/abajo hacen que el color "nazca"
          del blanco de la página en vez de cortar de golpe. */}
      <section className="relative overflow-hidden sidebar-degradado bg-gradient-to-r from-azul-medio via-azul-trazo to-azul-profundo py-6">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-5 bg-gradient-to-b from-white/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-5 bg-gradient-to-t from-white/70 to-transparent" />
        <div className="relative mx-auto flex max-w-6xl items-center justify-center gap-2 px-5 text-center">
          <Users size={20} className="hidden shrink-0 text-white/90 sm:block" />
          <p className="text-sm font-semibold text-white sm:text-base">
            Forma parte de los{" "}
            <span className="text-xl font-extrabold text-white sm:text-2xl">
              +<ContadorNegocios />
            </span>{" "}
            negocios que ya usan Mascotas Vet
          </p>
        </div>
      </section>

      {/* Franja de confianza */}
      <section className="bg-azul-claro/50">
        <Revelar>
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-5 py-10 text-center sm:grid-cols-4">
            {[
              { icono: Boxes, texto: "14 módulos integrados" },
              { icono: BellRing, texto: "Notificaciones automáticas" },
              { icono: Smartphone, texto: "Portal para tus clientes" },
              { icono: Award, texto: "Certificados oficiales" },
            ].map((f, i) => (
              <div key={i} className="group flex flex-col items-center gap-2">
                <span className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-white ${ACENTOS[i % 3].fuerte} shadow-sm transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-md`}>
                  <f.icono size={20} />
                </span>
                <p className="text-xs font-medium text-slate-500 sm:text-sm">{f.texto}</p>
              </div>
            ))}
          </div>
        </Revelar>
      </section>

      {/* Módulos */}
      <section id="modulos" className="mx-auto max-w-6xl px-5 py-20">
        <Revelar>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-marca font-black text-3xl text-slate-800">Todo lo que tu negocio necesita, ya integrado</h2>
            <p className="mt-3 text-slate-500">
              Nada de contratar cinco sistemas distintos. Activa solo los módulos que uses — el resto se queda apagado
              sin estorbar.
            </p>
          </div>
        </Revelar>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {MODULOS.map((m, i) => {
            const a = ACENTOS[i % 3];
            return (
              <Revelar key={m.titulo} className={i % 3 === 1 ? "sm:translate-y-3" : ""}>
                <div className={`group h-full rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-lg ${a.borde}`}>
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${a.suave} ${a.fuerte} transition-colors duration-300 ${a.solidoAlHover} group-hover:text-white`}>
                    <m.icono size={20} />
                  </span>
                  <h3 className="mt-4 font-semibold text-slate-800">{m.titulo}</h3>
                  <p className="mt-1.5 text-sm text-slate-500">{m.texto}</p>
                </div>
              </Revelar>
            );
          })}
        </div>
      </section>

      {/* Portal del cliente */}
      <section id="portal" className="bg-gradient-to-b from-azul-claro/60 to-white py-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-5 md:grid-cols-2">
          <Revelar className="order-2 md:order-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-azul-profundo shadow-sm">
              <Smartphone size={13} /> Portal del cliente incluido
            </span>
            <h2 className="mt-4 font-marca font-black text-3xl text-slate-800">
              Tus clientes ven el expediente de su mascota sin instalar nada
            </h2>
            <p className="mt-4 text-slate-500">
              Cada vez que das de alta una mascota, se genera automáticamente un expediente digital con un enlace
              propio — el tutor lo abre desde cualquier celular, sin crear cuenta ni descargar una app.
            </p>
            <ul className="mt-6 space-y-4">
              {[
                { t: "Notificaciones automáticas", d: "Tus clientes reciben WhatsApp y correo cuando agendas una cita, se acerca una vacuna o das de alta un nuevo expediente." },
                { t: "Certificados listos para imprimir", d: "Certificado de vacunación y certificado de salud (útil para trámites de aerolínea), con los datos y firma de tu veterinario." },
                { t: "Fotos, edad y raza de un vistazo", d: "El portal calcula la edad automáticamente y muestra información general de la raza." },
                { t: "Todo bajo tu marca", d: "El portal se ve con el logo y el color de tu negocio, no con el nuestro." },
              ].map((it) => (
                <li key={it.t} className="flex gap-3">
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-azul-trazo" />
                  <div>
                    <p className="font-medium text-slate-700">{it.t}</p>
                    <p className="text-sm text-slate-500">{it.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Revelar>
          <Revelar className="order-1 mx-auto max-w-xs md:order-2">
            <div className="transition-transform duration-500 hover:-translate-y-1 hover:scale-105">
              <IlustracionPortal />
            </div>
          </Revelar>
        </div>
      </section>

      {/* Consultar expediente de mi mascota */}
      <section id="mi-expediente" className="mx-auto max-w-3xl px-5 py-20">
        <Revelar>
          <div className="rounded-3xl border border-azul-claro bg-gradient-to-br from-azul-claro to-white p-8 text-center sm:p-10">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-azul-trazo to-azul-suave text-white">
              <Search size={22} />
            </span>
            <h2 className="mt-4 font-marca font-black text-2xl text-slate-800 sm:text-3xl">
              Quiero consultar el expediente de mi mascota
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm text-slate-500">
              ¿Ya diste de alta a tu mascota en alguna veterinaria de la red Mascotas pero perdiste el enlace? Escribe tu
              correo y teléfono y te llevamos directo a su expediente.
            </p>

            {expedientesEncontrados ? (
              <div className="mx-auto mt-6 max-w-sm space-y-2 text-left">
                <p className="text-center text-sm text-slate-500">Encontramos varias mascotas con esos datos:</p>
                {expedientesEncontrados.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => irAExpediente(m.negocioSubdominio, m.tokenPublico)}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:border-azul-suave hover:bg-azul-claro"
                  >
                    <span>
                      {m.nombre} <span className="font-normal text-slate-400">· {m.especie.toLowerCase()} · {m.negocioNombre}</span>
                    </span>
                    <ArrowRight size={15} className="shrink-0 text-azul-trazo" />
                  </button>
                ))}
              </div>
            ) : (
              <form onSubmit={buscarExpediente} className="mx-auto mt-6 max-w-sm space-y-3 text-left">
                <div>
                  <label className={CAMPO_LABEL}>Correo electrónico</label>
                  <input
                    type="email"
                    required
                    className={CAMPO_INPUT}
                    value={contacto.email}
                    onChange={(e) => setContacto({ ...contacto, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className={CAMPO_LABEL}>Teléfono</label>
                  <input
                    type="tel"
                    required
                    className={CAMPO_INPUT}
                    value={contacto.telefono}
                    onChange={(e) => setContacto({ ...contacto, telefono: e.target.value })}
                  />
                </div>
                {sinResultadosExpediente && (
                  <p className="text-sm text-amber-600">
                    No encontramos ninguna mascota con ese correo y teléfono. Revisa que sean los mismos datos que diste
                    de alta en tu veterinaria.
                  </p>
                )}
                {errorExpediente && <p className="text-sm text-red-600">{errorExpediente}</p>}
                <button type="submit" className={`${BTN_PRIMARIO} w-full`} disabled={buscandoExpediente}>
                  {buscandoExpediente ? <Loader2 size={16} className="animate-spin" /> : <Search size={15} />}
                  Buscar mi expediente
                </button>
              </form>
            )}
          </div>
        </Revelar>
      </section>

      {/* Precios */}
      <section id="precios" className="mx-auto max-w-5xl px-5 py-20">
        <Revelar>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-marca font-black text-3xl text-slate-800">Precio de plataforma grande, pagado como negocio chico</h2>
            <p className="mt-3 text-slate-500">Un solo plan, todos los módulos incluidos. Sin letras chiquitas.</p>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-4 py-1.5 text-xs font-semibold text-amber-700">
              🎉 Promoción especial por tiempo limitado
            </span>
          </div>
        </Revelar>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Revelar>
            <div className="relative h-full scale-[1.03] rounded-3xl border-2 border-azul-trazo bg-white p-8 shadow-lg shadow-azul-claro transition-shadow hover:shadow-xl">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-azul-trazo to-azul-suave px-4 py-1 text-xs font-semibold text-white shadow-sm">
                ⭐ La más elegida
              </span>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-500">Mensual</p>
                <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-600">-64% hoy</span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-lg text-slate-300 line-through">$699</span>
                <span className="font-marca font-black text-5xl bg-gradient-to-r from-azul-trazo to-azul-suave bg-clip-text text-transparent">$249</span>
                <span className="text-sm text-slate-400">/ mes</span>
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-azul-medio">
                <CheckCircle2 size={14} /> Cancela cuando quieras, sin contratos ni penalizaciones
              </p>
              <button type="button" onClick={() => abrirModalAlta("MENSUAL", "Precios Mensual")} className={`${BTN_PRIMARIO} mt-6 w-full`}>
                <Rocket size={15} /> Ya quiero usarlo
              </button>
            </div>
          </Revelar>
          <Revelar>
            <div className="h-full rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-500">Anual</p>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">Ahorras 1 mes</span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-lg text-slate-300 line-through">$8,388</span>
                <span className="font-marca font-black text-4xl text-slate-800">$2,739</span>
                <span className="text-sm text-slate-400">/ año</span>
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-azul-medio">
                <CheckCircle2 size={14} /> Cancela cuando quieras, sin contratos ni penalizaciones
              </p>
              <button type="button" onClick={() => abrirModalAlta("ANUAL", "Precios Anual")} className={`${BTN_SECUNDARIO} mt-6 w-full`}>
                <Rocket size={15} /> Ya quiero usarlo
              </button>
            </div>
          </Revelar>
        </div>
      </section>

      {/* Opiniones (testimonios simulados) */}
      <section id="opiniones" className="bg-gradient-to-b from-white via-azul-claro/40 to-white py-20">
        <div className="mx-auto max-w-6xl px-5">
          <Revelar>
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="font-marca font-black text-3xl text-slate-800">Negocios como el tuyo ya confían en Mascotas</h2>
              <p className="mt-3 text-slate-500">
                El software mas completo y de mayor aceptación en pequeños y medianos negocios.
              </p>
            </div>
          </Revelar>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TESTIMONIOS.map((t, i) => (
              <Revelar key={t.nombre}>
                <div className="h-full rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md">
                  <div className="flex gap-0.5 text-amber-400">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-slate-600">"{t.texto}"</p>
                  <div className="mt-5 flex items-center gap-3">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${ACENTOS[i % 3].solido} text-xs font-semibold text-white`}>
                      {t.iniciales}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-700">{t.nombre}</p>
                      <p className="text-xs text-slate-400">{t.negocio}</p>
                    </div>
                  </div>
                </div>
              </Revelar>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">
            Unete a la red de Mascotas, donde los clientes valoran la verdadera experiencia de un servicio profesional.
          </p>
        </div>
      </section>

      {/* Formulario de demo */}
      <section id="demo" className="relative overflow-hidden bg-gradient-to-br from-azul-profundo via-azul-trazo to-azul-profundo py-20">
        <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-azul-suave/10 blur-3xl animar-flotar-lento" />
        <div className="pointer-events-none absolute -bottom-24 left-0 h-72 w-72 rounded-full bg-azul-medio/10 blur-3xl animar-flotar-lento" style={{ animationDelay: "3s" }} />
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-12 px-5 md:grid-cols-2">
          <Revelar>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-azul-suave">
              <PawPrint size={13} /> Cuenta demo gratis
            </span>
            <h2 className="mt-4 font-marca font-black text-3xl text-white">Pruébalo con los datos reales de tu negocio</h2>
            <p className="mt-4 text-slate-300">
              Cuéntanos un poco de tu negocio y te contactamos para activar tu cuenta demo — sin costo y sin
              compromiso.
            </p>
            <div className="mt-8 space-y-3 text-sm text-slate-300">
              <p className="flex items-center gap-2"><Mail size={16} className="text-azul-suave" /> soporte@mascotas.vet</p>
              <p className="flex items-center gap-2"><Phone size={16} className="text-azul-suave" /> Te contactamos por el medio que prefieras</p>
            </div>
          </Revelar>

          <Revelar>
            <div className="rounded-3xl bg-white p-7 shadow-xl">
              {enviado ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <CheckCircle2 size={40} className="text-azul-trazo" />
                  <p className="font-semibold text-slate-800">¡Listo! Recibimos tu solicitud.</p>
                  <p className="text-sm text-slate-500">Te contactaremos muy pronto para activar tu cuenta demo.</p>
                </div>
              ) : (
                <form onSubmit={enviarSolicitud} className="space-y-4">
                  <div>
                    <label className={CAMPO_LABEL}>Tu nombre</label>
                    <input className={CAMPO_INPUT} required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
                  </div>
                  <div>
                    <label className={CAMPO_LABEL}>Nombre de tu negocio</label>
                    <input className={CAMPO_INPUT} required value={form.negocio} onChange={(e) => setForm({ ...form, negocio: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={CAMPO_LABEL}>Teléfono</label>
                      <input className={CAMPO_INPUT} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
                    </div>
                    <div>
                      <label className={CAMPO_LABEL}>Correo</label>
                      <input type="email" className={CAMPO_INPUT} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className={CAMPO_LABEL}>Cuéntanos de tu negocio (opcional)</label>
                    <textarea className={CAMPO_INPUT} rows={3} value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} />
                  </div>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  <button type="submit" className={`${BTN_PRIMARIO} w-full !py-3`} disabled={enviando}>
                    {enviando ? "Enviando..." : "Solicitar cuenta demo"}
                  </button>
                </form>
              )}
            </div>
          </Revelar>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-white to-azul-claro/50 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-sm text-slate-400 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src="/logo-icono.png" alt="Mascotas Vet" className="h-7 w-7" />
            <span className="font-marca font-black text-slate-600">Mascotas <span className="font-light text-gris-texto">Vet</span></span>
          </div>
          <p>© {new Date().getFullYear()} Mascotas Vet. Todos los derechos reservados.</p>
          <div className="flex items-center gap-4">
            <Link to="/tutoriales" className="hover:text-azul-trazo">Tutoriales</Link>
            <Link to="/aviso-de-privacidad" className="hover:text-azul-trazo">Aviso de privacidad</Link>
            <a href="mailto:soporte@mascotas.vet" className="hover:text-azul-trazo">soporte@mascotas.vet</a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-semibold text-emerald-600 hover:text-emerald-700"
            >
              <MessageCircle size={15} /> ¿Puedo ayudarte?
            </a>
          </div>
        </div>
      </footer>

      {/* Botón flotante de WhatsApp */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Escríbenos por WhatsApp"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-emerald-600 hover:shadow-xl"
      >
        <MessageCircle size={22} />
        <span className="hidden sm:inline">¿Puedo ayudarte?</span>
      </a>

      {/* Modal "Ya soy cliente" */}
      {modalClienteAbierto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" onClick={cerrarModalCliente}>
          <div className="w-full max-w-sm rounded-3xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-marca font-black text-xl text-slate-800">Ya soy cliente</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Escribe tu correo y te llevamos directo al login de tu negocio.
                </p>
              </div>
              <button type="button" onClick={cerrarModalCliente} className="text-slate-400 hover:text-slate-600" aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>

            {negociosEncontrados ? (
              <div className="mt-5 space-y-2">
                <p className="text-sm text-slate-500">Encontramos más de un negocio con ese correo. Elige el tuyo:</p>
                {negociosEncontrados.map((n) => (
                  <button
                    key={n.subdominio}
                    type="button"
                    onClick={() => irALogin(n.subdominio)}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-4 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:border-azul-suave hover:bg-azul-claro"
                  >
                    {n.nombre} <ArrowRight size={15} className="text-azul-trazo" />
                  </button>
                ))}
              </div>
            ) : (
              <form onSubmit={buscarNegocio} className="mt-5 space-y-4">
                <div>
                  <label className={CAMPO_LABEL}>Correo electrónico</label>
                  <input
                    type="email"
                    required
                    className={CAMPO_INPUT}
                    value={correoCliente}
                    onChange={(e) => setCorreoCliente(e.target.value)}
                    autoFocus
                  />
                </div>
                {errorCliente && <p className="text-sm text-red-600">{errorCliente}</p>}
                <button type="submit" className={`${BTN_PRIMARIO} w-full`} disabled={buscandoCliente}>
                  {buscandoCliente ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={15} />}
                  Continuar
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal "Ya quiero usarlo": alta de negocio con pago en línea, en dos
          pasos cortos (datos del negocio, luego domicilio) para que no se
          vea como un formulario interminable ni en celular ni en pc. */}
      {modalAltaAbierto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/50 px-4 py-8"
          onClick={() => setModalAltaAbierto(false)}
        >
          <div className="w-full max-w-lg rounded-3xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-marca font-black text-xl text-slate-800">Crea tu negocio en Mascotas</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {pasoAlta === 1
                    ? "Primero tus datos y los de tu negocio."
                    : "Ahora el domicilio de tu negocio — con esto queda listo para pagar."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalAltaAbierto(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            </div>

            {/* Indicador de paso */}
            <div className="mt-4 flex items-center gap-2">
              <div className={`h-1.5 flex-1 rounded-full ${pasoAlta >= 1 ? "bg-azul-trazo" : "bg-slate-100"}`} />
              <div className={`h-1.5 flex-1 rounded-full ${pasoAlta >= 2 ? "bg-azul-trazo" : "bg-slate-100"}`} />
            </div>
            <p className="mt-1.5 text-xs font-medium text-slate-400">Paso {pasoAlta} de 2</p>

            <form onSubmit={pasoAlta === 1 ? irAPaso2 : enviarAlta} className="mt-4 space-y-4">
              {pasoAlta === 1 ? (
                <>
                  <div>
                    <label className={CAMPO_LABEL}>Plan</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => cambiarAlta("plan", "MENSUAL")}
                        className={`rounded-xl border p-3 text-left text-sm transition-colors ${
                          formAlta.plan === "MENSUAL" ? "border-azul-trazo bg-azul-claro" : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <p className="font-semibold text-slate-700">Mensual</p>
                        <p className="text-slate-500">$249 / mes</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => cambiarAlta("plan", "ANUAL")}
                        className={`rounded-xl border p-3 text-left text-sm transition-colors ${
                          formAlta.plan === "ANUAL" ? "border-azul-trazo bg-azul-claro" : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <p className="font-semibold text-slate-700">Anual</p>
                        <p className="text-slate-500">$2,739 / año</p>
                      </button>
                    </div>
                  </div>

                  <div data-campo="nombreNegocio">
                    <label className={CAMPO_LABEL}>Nombre del negocio *</label>
                    <input
                      className={claseCampoAlta("nombreNegocio")}
                      value={formAlta.nombreNegocio}
                      onChange={(e) => cambiarAlta("nombreNegocio", e.target.value)}
                    />
                    <ErrorAlta campo="nombreNegocio" />
                  </div>

                  <div data-campo="subdominio">
                    <label className={`${CAMPO_LABEL} flex items-center gap-1.5`}>
                      Subdominio (para tu portal) *
                      <button
                        type="button"
                        onClick={() => setAyudaSubdominioAbierta((v) => !v)}
                        className="text-slate-400 hover:text-azul-trazo"
                        aria-label="Ayuda sobre el subdominio"
                      >
                        <HelpCircle size={14} />
                      </button>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        className={claseCampoAlta("subdominio")}
                        placeholder="miclinica"
                        value={formAlta.subdominio}
                        onChange={(e) => cambiarSubdominio(e.target.value)}
                      />
                      <span className="whitespace-nowrap text-sm text-slate-400">.{BASE_DOMAIN}</span>
                    </div>
                    {ayudaSubdominioAbierta && (
                      <p className="mt-1.5 rounded-lg bg-azul-claro p-2 text-xs text-azul-profundo">
                        Es la dirección de tu portal: debe ir sin espacios, sin acentos, sin comas y sin símbolos —
                        solo minúsculas, números y guiones (ej. <strong>miclinica</strong> o{" "}
                        <strong>clinica-sanangel</strong>).
                      </p>
                    )}
                    <ErrorAlta campo="subdominio" />
                  </div>

                  <div>
                    <label className={CAMPO_LABEL}>Tipo de negocio *</label>
                    <select
                      className={CAMPO_INPUT}
                      value={formAlta.tipoNegocio}
                      onChange={(e) => cambiarAlta("tipoNegocio", e.target.value)}
                    >
                      {TIPOS_NEGOCIO.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div data-campo="nombreAdmin">
                      <label className={CAMPO_LABEL}>Tu nombre *</label>
                      <input
                        className={claseCampoAlta("nombreAdmin")}
                        value={formAlta.nombreAdmin}
                        onChange={(e) => cambiarAlta("nombreAdmin", e.target.value)}
                      />
                      <ErrorAlta campo="nombreAdmin" />
                    </div>
                    <div data-campo="telefono">
                      <label className={CAMPO_LABEL}>Teléfono *</label>
                      <input
                        type="tel"
                        className={claseCampoAlta("telefono")}
                        value={formAlta.telefono}
                        onChange={(e) => cambiarAlta("telefono", e.target.value)}
                      />
                      <ErrorAlta campo="telefono" />
                    </div>
                  </div>

                  <div data-campo="email">
                    <label className={CAMPO_LABEL}>Correo electrónico *</label>
                    <input
                      type="email"
                      className={claseCampoAlta("email")}
                      value={formAlta.email}
                      onChange={(e) => cambiarAlta("email", e.target.value)}
                    />
                    <ErrorAlta campo="email" />
                  </div>

                  <div data-campo="password">
                    <label className={CAMPO_LABEL}>Contraseña *</label>
                    <input
                      type="password"
                      className={claseCampoAlta("password")}
                      minLength={6}
                      value={formAlta.password}
                      onChange={(e) => cambiarAlta("password", e.target.value)}
                    />
                    <ErrorAlta campo="password" />
                  </div>

                  {errorAlta && <p className="text-sm text-red-600">{errorAlta}</p>}

                  <button type="submit" className={`${BTN_PRIMARIO} w-full !py-3`}>
                    Continuar <ArrowRight size={16} />
                  </button>
                </>
              ) : (
                <>
                  <DomicilioCampos
                    valores={domicilioAlta}
                    onChange={cambiarDomicilioAlta}
                    errores={{
                      calle: erroresAlta.calle,
                      numero: erroresAlta.numero,
                      codigoPostal: erroresAlta.codigoPostal,
                      colonia: erroresAlta.colonia,
                      ciudad: erroresAlta.ciudad,
                      estado: erroresAlta.estado,
                      pais: erroresAlta.pais,
                    }}
                  />
                  <p className="text-xs text-slate-400">
                    Con esto tu negocio queda precargado desde el primer momento — no tendrás que volver a capturarlo.
                  </p>

                  {errorAlta && <p className="text-sm text-red-600">{errorAlta}</p>}

                  <div className="flex gap-3">
                    <button type="button" onClick={volverAPaso1} className={`${BTN_SECUNDARIO} !px-4`}>
                      <ArrowLeft size={16} /> Atrás
                    </button>
                    <button
                      type="submit"
                      className={`${BTN_PRIMARIO} flex-1 !py-3`}
                      disabled={!domicilioAltaCompleto || enviandoAlta}
                    >
                      {enviandoAlta ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                      {enviandoAlta ? "Redirigiendo a pago..." : "Continuar al pago"}
                    </button>
                  </div>
                  <p className="text-center text-xs text-slate-400">
                    Pago seguro procesado por Stripe. Tu negocio se activa en cuanto se confirme el pago.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
